import { Injectable, NgZone } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import * as fromActions from './auth.actions';
import {
  selectAuthenticatedAt,
  selectAuthenticationResult,
  selectIsAuthenticated,
  selectIsAuthenticatedInAppSuccess
} from './auth.selectors';
import { catchError, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, firstValueFrom, of, timer } from 'rxjs';
import { AuthService } from '../service';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AppPushNotification, UserRoleEnum } from '@qvs/core';
import { ActionSheetController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { appDeepLinkClicked, appPushNotificationClicked, resetAppStartedByDeepLinkClick, resume } from '~/modules/app/slice';
import { removeAllChatMessagesFromNotRead, updateAllMessagesReadStatus } from '~/modules/chat/slice';
import { clearCurrentClient, loadUserProfile, selectCrmMyContacts, selectUserRole, setCurrentClient } from '@qvs/core/user';
import { environment } from '#environment';
import { FileCacheService } from '~/shared/services/file-cache.service';
import { loadEnvironment } from '@qvs/core/env';
import { loadRemoteConfig } from '@qvs/core/remoteConfig';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class AuthEffects {
  authenticateByContractNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.authenticateByContractNumber),
      switchMap((action) =>
        this.authService.authenticateByContractNumber(action.contractNumber).pipe(
          switchMap((authenticationResult) =>
            authenticationResult?.isAuthenticated
              ? of(
                  fromActions.createAuthenticationSuccess({
                    authenticationResult,
                    contractNumber: action.contractNumberPrefixed,
                    authenticatedAt: Date.now()
                  })
                )
              : of(fromActions.authenticateByContractNumberFailure({ authenticationResult }))
          ),
          catchError((error) =>
            of(fromActions.authenticateByContractNumberFailure({ authenticationResult: { isServerError: true }, error }))
          )
        )
      )
    )
  );

  authenticateByUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.authenticateByUserNamePassword),
      switchMap((action) =>
        this.authService.authenticateByUserPassword(action.username, action.password).pipe(
          switchMap((token) =>
            of(
              fromActions.createAuthenticationSuccess({
                authenticationResult: { isAuthenticated: true, isCanceled: false, accessToken: token },
                contractNumber: action.usernamePrefixed,
                authenticatedAt: Date.now()
              })
            )
          ),
          catchError(() => of(fromActions.authenticateByUserNamePasswordFailure()))
        )
      )
    )
  );

  activateClientSideSessionTimeoutAfterAuthenticationSuccess$ = createEffect(() => {
    const interruption$ = this.actions$.pipe(ofType(fromActions.logout, fromActions.cancelAuthentication));
    const initiateClientSideSessionTimeout = () =>
      timer(this.CLIENT_SIDE_SESSION_TIMEOUT).pipe(
        takeUntil(interruption$),
        tap(() => this.router.navigateByUrl('/login/session-timed-out')),
        map(() => fromActions.logout())
      );
    return this.actions$.pipe(
      ofType(fromActions.createAuthenticationSuccess),
      filter(() => environment.featureFlags.isClientSideSessionTimeoutEnabled),
      switchMap(initiateClientSideSessionTimeout)
    );
  });

  // JS timers are frozen while the device is locked and no session check is possible offline,
  // so on resume the time since authentication is compared with the client side session timeout.
  checkClientSideSessionTimeoutOnResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resume),
      filter(() => environment.featureFlags.isClientSideSessionTimeoutEnabled),
      concatLatestFrom(() => this.store.select(selectAuthenticatedAt)),
      filter(
        ([_action, authenticatedAt]) => !!authenticatedAt && Date.now() - authenticatedAt > this.CLIENT_SIDE_SESSION_TIMEOUT
      ),
      tap(() => this.router.navigateByUrl('/login/session-timed-out')),
      map(() => fromActions.logout())
    )
  );

  cancelAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.cancelAuthentication),
      withLatestFrom(this.store.select(selectAuthenticationResult)),
      switchMap(([_action, authenticationResult]) =>
        combineLatest([
          this.authService.logout(authenticationResult).pipe(catchError(() => of(null))),
          this.ngZone.run(() => this.router.navigateByUrl('/login', { replaceUrl: true }).catch())
        ]).pipe(switchMap(() => of(fromActions.logoutSuccess())))
      )
    )
  );

  logoutComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.logoutSuccess, fromActions.logoutFailure),
      switchMap(() =>
        of(
          clearCurrentClient(),
          loadEnvironment({ environmentConfiguration: environment.configuration }),
          loadRemoteConfig(),
          resetAppStartedByDeepLinkClick()
        )
      )
    )
  );

  clearFileCache$ = createEffect(
    () =>
      this.actions$.pipe(
        // CITEGESTIONMOBILEAPPSH-169: clear file cache on logout & application startup
        ofType(fromActions.logoutSuccess, fromActions.logoutFailure, ROOT_EFFECTS_INIT),
        tap(() => this.fileCacheService.clearFileCache())
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.logout),
      withLatestFrom(this.store.select(selectAuthenticationResult)),
      tap(() => this.closeAllUiDialogs()),
      switchMap(([_action, authenticationResult]) =>
        this.authService.logout(authenticationResult).pipe(
          switchMap(() => of(fromActions.logoutSuccess())),
          catchError(() => of(fromActions.logoutFailure()))
        )
      )
    )
  );

  acceptConsentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.acceptConsent),
      withLatestFrom(this.store.pipe(select(selectUserRole))),
      switchMap(([_action, userRole]) => {
        const url = userRole === UserRoleEnum.CLIENT_CRM ? '/pages/home-crm' : '/pages/home';
        return combineLatest([this.router.navigateByUrl(url, { replaceUrl: true })]).pipe(
          switchMap(() => of(fromActions.acceptConsentSuccess()))
        );
      })
    )
  );

  // TODO (MOB-770): get from BE once available, for now we use a fixed value of 15 minutes
  private readonly CLIENT_SIDE_SESSION_TIMEOUT = 15 * 60 * 1000;

  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly fileCacheService: FileCacheService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly ngZone: NgZone,
    private readonly platform: Platform,
    private readonly modalController: ModalController,
    private readonly popoverController: PopoverController,
    private readonly actionSheetController: ActionSheetController
  ) {
    combineLatest([
      this.store.select(selectUserRole),
      this.store.select(appPushNotificationClicked),
      this.store.select(appDeepLinkClicked),
      this.store.select(selectIsAuthenticated),
      this.store.select(selectIsAuthenticatedInAppSuccess)
    ]).subscribe(([userRole, pushNotification, deepLink, isAuthenticated, isAuthenticatedInAppSuccess]) => {
      if (isAuthenticatedInAppSuccess) {
        return;
      }
      const authentication = environment.accessApp.deepLinks.authentication;
      if (deepLink?.url === authentication.cancel) {
        this.store.dispatch(fromActions.cancelAuthentication());
      }
      if (deepLink?.url === authentication.success) {
        this.store.dispatch(fromActions.createAuthenticationFromAccessAppSuccess());
      }
      if (!userRole && isAuthenticated) {
        this.store.dispatch(loadUserProfile());
      }
      if (userRole) {
        if (!this.platform.is('hybrid') && this.router.getCurrentNavigation()?.extractedUrl.toString().startsWith('/pages/')) {
          // -> in-browser development mode
        } else {
          if (isAuthenticated && userRole === UserRoleEnum.CLIENT) {
            this.redirectAfterAuthenticatedClient(pushNotification);
          }
          if (isAuthenticated && userRole === UserRoleEnum.CLIENT_CRM) {
            this.redirectAfterAuthenticatedCrmUser(pushNotification);
          }
        }
      }
    });
  }

  private async closeAllUiDialogs() {
    await Promise.all([
      this.dismissIfPresent(this.modalController),
      this.dismissIfPresent(this.popoverController),
      this.dismissIfPresent(this.actionSheetController)
    ]);
  }

  private async dismissIfPresent(controller: ModalController | PopoverController | ActionSheetController) {
    const dialog = await controller.getTop();
    if (dialog) {
      await controller.dismiss();
      await this.dismissIfPresent(controller);
    }
  }

  private redirectAfterAuthenticatedCrmUser(pushNotification: AppPushNotification) {
    if (pushNotification?.data?.senderStaffId && pushNotification?.data?.authenticated) {
      return this.ngZone.run(() => {
        this.router.navigateByUrl('/pages/chat', { replaceUrl: true }).finally(() => {
          this.store.dispatch(fromActions.redirectedAfterAuthenticationSuccess());
          firstValueFrom(this.store.select(selectCrmMyContacts)).then((contacts) => {
            const client = contacts.find((contact) => contact.staffId === pushNotification.data.senderStaffId);
            if (client) {
              this.store.dispatch(setCurrentClient({ client }));
              this.store.dispatch(removeAllChatMessagesFromNotRead({ authorId: client.staffId }));
              this.store.dispatch(updateAllMessagesReadStatus({ authorId: client.staffId }));
            }
          });
        });
      });
    }

    return this.ngZone.run(() => {
      this.router.navigateByUrl('/pages/home-crm', { replaceUrl: true }).finally(() => {
        this.store.dispatch(fromActions.redirectedAfterAuthenticationSuccess());
      });
    });
  }

  private redirectAfterAuthenticatedClient(pushNotification: AppPushNotification) {
    if (pushNotification?.data?.senderStaffId) {
      return this.ngZone.run(() => {
        this.router.navigateByUrl('/pages/chat', { replaceUrl: true }).finally(() => {
          this.store.dispatch(removeAllChatMessagesFromNotRead({ authorId: pushNotification.data.senderStaffId }));
          this.store.dispatch(updateAllMessagesReadStatus({ authorId: pushNotification.data.senderStaffId }));
          this.store.dispatch(fromActions.redirectedAfterAuthenticationSuccess());
        });
      });
    }
    return this.ngZone.run(async () => {
      try {
        await this.router.navigateByUrl('/pages/home', { replaceUrl: true });
      } finally {
        this.store.dispatch(fromActions.redirectedAfterAuthenticationSuccess());
      }
    });
  }
}
